import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { EDITION, isIndia, isEdu } from "@/lib/edition";
import {
  MagnifyingGlass, Buildings, UsersThree, Flower, User, ArrowRight, SpinnerGap, X, Sparkle,
} from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import AddInstitutionRequestDialog from "@/components/AddInstitutionRequestDialog";

const EMPTY = { institutions: [], groups: [], memorials: [], people: [], did_you_mean: "", counts: {} };
const IS_INDIA_ED = isIndia || isEdu;

export function GlobalSearch({ trigger = "icon", className = "" }) {
  const [open, setOpen] = useState(false);
  const [reqOpen, setReqOpen] = useState(false);
  const [q, setQ] = useState("");
  const [res, setRes] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const inputRef = useRef(null);
  const timer = useRef(null);

  const openRequest = () => { setOpen(false); setReqOpen(true); };

  const run = useCallback((term) => {
    if (!term || term.trim().length < 2) { setRes(EMPTY); setLoading(false); return; }
    setLoading(true);
    api.get("/public/search", { params: { q: term.trim(), limit: 6, country: EDITION.country || "" } })
      .then((r) => setRes(r.data || EMPTY))
      .catch(() => setRes(EMPTY))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!open) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => run(q), 250);
    return () => clearTimeout(timer.current);
  }, [q, open, run]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 60);
    else { setQ(""); setRes(EMPTY); }
  }, [open]);

  const go = (path) => { setOpen(false); nav(path); };
  const onSubmit = (e) => {
    e.preventDefault();
    if (q.trim().length >= 2) go(`/explore/institutions?q=${encodeURIComponent(q.trim())}`);
  };

  const total = (res.institutions?.length || 0) + (res.groups?.length || 0) +
    (res.memorials?.length || 0) + (res.people?.length || 0);
  const hasQuery = q.trim().length >= 2;

  const Section = ({ icon: Icon, label, items, render }) =>
    items && items.length > 0 ? (
      <div className="py-1.5">
        <p className="flex items-center gap-1.5 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          <Icon size={13} weight="fill" /> {label}
        </p>
        <div>{items.map(render)}</div>
      </div>
    ) : null;

  const Row = ({ onClick, primary, secondary, thumb, testid }) => (
    <button type="button" onClick={onClick} data-testid={testid}
      className="flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-accent">
      <span className="grid h-8 w-8 shrink-0 place-items-center overflow-hidden rounded-lg bg-muted">
        {thumb}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-foreground">{primary}</span>
        {secondary && <span className="block truncate text-xs text-muted-foreground">{secondary}</span>}
      </span>
      <ArrowRight size={14} className="shrink-0 text-muted-foreground" />
    </button>
  );

  return (
    <>
      {trigger === "full" ? (
        <button type="button" onClick={() => setOpen(true)} data-testid="global-search-open-full"
          className={`flex w-full items-center gap-2 rounded-full border border-border bg-muted/40 px-4 py-2 text-sm text-muted-foreground transition-colors hover:border-primary/40 ${className}`}>
          <MagnifyingGlass size={16} /> Search {EDITION.brand}…
        </button>
      ) : (
        <button type="button" onClick={() => setOpen(true)} aria-label="Search" data-testid="global-search-open"
          className={`grid h-9 w-9 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-accent hover:text-foreground ${className}`}>
          <MagnifyingGlass size={18} />
        </button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="gap-0 overflow-hidden p-0 sm:max-w-xl" data-testid="global-search-dialog">
          <DialogTitle className="sr-only">Search {EDITION.brand}</DialogTitle>
          <DialogDescription className="sr-only">Search across institutions, alumni groups, memorials and alumni.</DialogDescription>
          <form onSubmit={onSubmit} className="flex items-center gap-2 border-b border-border px-4">
            <MagnifyingGlass size={18} className="shrink-0 text-muted-foreground" />
            <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} data-testid="global-search-input"
              placeholder={`Search ${EDITION.brand} — institutions, groups, memorials, alumni…`}
              className="h-14 w-full bg-transparent text-base outline-none placeholder:text-muted-foreground" />
            {loading ? <SpinnerGap size={18} className="animate-spin text-muted-foreground" />
              : q && <button type="button" onClick={() => setQ("")} className="text-muted-foreground hover:text-foreground"><X size={16} /></button>}
          </form>

          <div className="max-h-[62vh] overflow-y-auto" data-testid="global-search-results">
            {hasQuery && res.did_you_mean && res.did_you_mean.toLowerCase() !== q.trim().toLowerCase() && (
              <button type="button" onClick={() => setQ(res.did_you_mean)} data-testid="global-search-did-you-mean"
                className="flex w-full items-center gap-1.5 border-b border-border bg-accent/40 px-4 py-2.5 text-left text-sm">
                <Sparkle size={14} weight="fill" className="text-primary" />
                <span className="text-muted-foreground">Did you mean</span>
                <span className="font-semibold text-primary">{res.did_you_mean}</span>
                <ArrowRight size={13} className="ml-auto shrink-0 text-muted-foreground" />
              </button>
            )}
            {!hasQuery ? (
              <p className="px-4 py-10 text-center text-sm text-muted-foreground">Type at least 2 characters to search across the whole platform.</p>
            ) : !loading && total === 0 ? (
              <div className="px-4 py-10 text-center">
                <p className="text-sm text-muted-foreground">No results for “{q.trim()}”.</p>
                {IS_INDIA_ED ? (
                  <>
                    <p className="mx-auto mt-2 max-w-xs text-xs text-muted-foreground">Studied somewhere we don't list yet — even a school that no longer exists? Add it and we'll build its page.</p>
                    <button onClick={openRequest} className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90" data-testid="global-search-add-institution">
                      <Buildings size={15} weight="fill" /> Add your institution
                    </button>
                  </>
                ) : (
                  <button onClick={() => go("/institutions/suggest")} className="mt-3 text-sm font-medium text-primary hover:underline" data-testid="global-search-suggest">
                    Suggest an institution →
                  </button>
                )}
              </div>
            ) : (
              <>
                <Section icon={Buildings} label="Institutions" items={res.institutions} render={(i) => (
                  <Row key={i.id} testid={`search-inst-${i.id}`} onClick={() => go(`/institutions/${i.id}`)}
                    primary={i.name} secondary={[i.type, i.location || i.country].filter(Boolean).join(" · ")}
                    thumb={i.logo_url ? <img src={i.logo_url} alt="" className="h-full w-full object-cover" /> : <Buildings size={16} className="text-green-600" />} />
                )} />
                <Section icon={User} label="Alumni" items={res.people} render={(p) => (
                  <Row key={p.user_id} testid={`search-person-${p.user_id}`} onClick={() => go(`/u/${p.user_id}`)}
                    primary={p.name} secondary={p.headline || p.location}
                    thumb={p.avatar_file_id ? <img src={fileUrl(p.avatar_file_id)} alt="" className="h-full w-full object-cover" /> : <User size={16} className="text-slate-500" />} />
                )} />
                <Section icon={UsersThree} label="Alumni Groups" items={res.groups} render={(g) => (
                  <Row key={g.id} testid={`search-group-${g.id}`} onClick={() => go(`/groups/${g.id}`)}
                    primary={g.name} secondary={`${g.member_count || 0} member${g.member_count === 1 ? "" : "s"}`}
                    thumb={<UsersThree size={16} className="text-orange-500" />} />
                )} />
                <Section icon={Flower} label="In Memoriam" items={res.memorials} render={(m) => (
                  <Row key={m.id} testid={`search-memorial-${m.id}`} onClick={() => go(`/memorials/${m.id}`)}
                    primary={m.name} secondary={[m.institution, m.class_batch].filter(Boolean).join(" · ")}
                    thumb={m.photo_url ? <img src={m.photo_url} alt="" className="h-full w-full object-cover" /> : <Flower size={16} className="text-primary" />} />
                )} />
                {total > 0 && (
                  <button type="button" onClick={onSubmit} data-testid="global-search-see-all"
                    className="flex w-full items-center justify-center gap-1.5 border-t border-border px-4 py-3 text-sm font-medium text-primary transition-colors hover:bg-accent">
                    See all institutions matching “{q.trim()}” <ArrowRight size={14} />
                  </button>
                )}
                {IS_INDIA_ED && (
                  <button type="button" onClick={openRequest} data-testid="global-search-add-institution-footer"
                    className="flex w-full items-center justify-center gap-1.5 border-t border-border px-4 py-2.5 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground">
                    <Buildings size={13} /> Not the one you meant? Add your institution
                  </button>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AddInstitutionRequestDialog open={reqOpen} onOpenChange={setReqOpen} prefillName={q.trim()} />
    </>
  );
}

export default GlobalSearch;
